import { BadgeColor } from '@digitale-menschen/component-library';
import { LexplusErfuellungsgradEnum } from '@lexplus-user-api';

/**
 * Every grade's label, including the two no bar draws a segment for.
 *
 * One source for every surface that names a grade: the bar's legend takes four of these, the
 * filter's dropdown offers all six, and the Anforderung card's badge reads the same table — so
 * a wording change cannot leave them disagreeing.
 *
 * A `Record` over the enum, so a grade added to the DTO fails to compile until it is named.
 */
export const ERFUELLUNGSGRAD_LABELS: Readonly<Record<LexplusErfuellungsgradEnum, string>> = {
  [LexplusErfuellungsgradEnum.NichtBearbeitet]: $localize`:@@compliance.erfuellungsgrad.nichtBearbeitet:Nicht bearbeitet`,
  [LexplusErfuellungsgradEnum.NichtErfuellt]: $localize`:@@compliance.erfuellungsgrad.nichtErfuellt:Nicht erfüllt`,
  [LexplusErfuellungsgradEnum.InAbklaerung]: $localize`:@@compliance.erfuellungsgrad.inAbklaerung:In Abklärung`,
  [LexplusErfuellungsgradEnum.Erfuellt]: $localize`:@@compliance.erfuellungsgrad.erfuellt:Erfüllt`,
  [LexplusErfuellungsgradEnum.NichtRelevant]: $localize`:@@compliance.erfuellungsgrad.nichtRelevant:Nicht relevant`,
  [LexplusErfuellungsgradEnum.NichtDefiniert]: $localize`:@@compliance.erfuellungsgrad.nichtDefiniert:Nicht definiert`,
};

/**
 * The colour a grade's badge takes — the Erfüllungsgrad bar's palette, so a badge and a bar
 * segment colour one grade the same way. `NichtDefiniert` is folded into `NichtBearbeitet` by
 * the backend and only covers the enum.
 */
export const ERFUELLUNGSGRAD_BADGE_COLORS: Readonly<Record<LexplusErfuellungsgradEnum, BadgeColor>> = {
  [LexplusErfuellungsgradEnum.NichtBearbeitet]: 'gray',
  [LexplusErfuellungsgradEnum.NichtErfuellt]: 'error',
  [LexplusErfuellungsgradEnum.InAbklaerung]: 'warning',
  [LexplusErfuellungsgradEnum.Erfuellt]: 'success',
  [LexplusErfuellungsgradEnum.NichtRelevant]: 'gray',
  [LexplusErfuellungsgradEnum.NichtDefiniert]: 'gray',
};

/**
 * The colour an icon takes in each badge colour: the strong `fg-*-primary` shades, which a line
 * icon needs on white where a bar segment's lighter fill would wash out. One table, so a grade's
 * icon reads the same in a list row and in the bar's legend.
 *
 * Passed as `dm-icon`'s `customiconcolor`: inside a `dm-button` a colour utility on the icon is
 * repainted with the button's own icon colour.
 */
export const BADGE_COLOR_ICON_COLORS: Readonly<Partial<Record<BadgeColor, string>>> = {
  success: 'var(--dm-color-fg-success-primary)',
  error: 'var(--dm-color-fg-error-primary)',
  warning: 'var(--dm-color-fg-warning-primary)',
  brand: 'var(--dm-color-fg-brand-primary)',
  gray: 'var(--dm-color-fg-quaternary)',
};

/**
 * The icon a grade's badge leads with, taken from `AUFGABE_STATUS_DISPLAY`'s ladder so the rungs
 * the two share read the same way. `NichtErfuellt` has no equivalent there and is the one
 * genuine failure, hence the exclamation.
 */
export const ERFUELLUNGSGRAD_BADGE_ICONS: Readonly<Record<LexplusErfuellungsgradEnum, string>> = {
  [LexplusErfuellungsgradEnum.NichtBearbeitet]: 'circle',
  [LexplusErfuellungsgradEnum.NichtErfuellt]: 'alert-circle',
  [LexplusErfuellungsgradEnum.InAbklaerung]: 'clock',
  [LexplusErfuellungsgradEnum.Erfuellt]: 'check-circle',
  [LexplusErfuellungsgradEnum.NichtRelevant]: 'minus-circle',
  [LexplusErfuellungsgradEnum.NichtDefiniert]: 'circle',
};
